import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, PlayCircle, Video as VideoIcon, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const VIDEO_CATEGORIES = ["Hutbe", "Ligjerata", "Kuran"];

const getYouTubeId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

const getCategoryLabel = (category) => {
  const normalizedCategory = category === 'hutbe'
    ? 'Hutbe'
    : category === 'ligjerate'
      ? 'Ligjerata'
      : category;
  return VIDEO_CATEGORIES.includes(normalizedCategory) ? normalizedCategory : category;
};

const VideosPage = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [playingVideo, setPlayingVideo] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const res = await axios.get(`${API}/videos`);
      setVideos(res.data);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = useMemo(() => VIDEO_CATEGORIES, []);

  const visibleVideos = useMemo(() => (
    activeCategory === 'all'
      ? videos
      : videos.filter((video) => getCategoryLabel(video.category) === activeCategory)
  ), [activeCategory, videos]);

  return (
    <main className="min-h-screen bg-background py-24">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-primary hover:text-primary-700 font-medium mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Kthehu
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <VideoIcon className="w-8 h-8 text-accent" />
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-primary">
            Video Galeria
          </h1>
        </div>

        {categories.length > 0 && (
          <div className="flex flex-wrap gap-3 mb-8">
            <button
              type="button"
              onClick={() => setActiveCategory('all')}
              className={`px-5 py-2 rounded-lg border font-medium transition-colors ${
                activeCategory === 'all'
                  ? 'bg-primary text-white border-primary'
                  : 'border-primary text-primary hover:bg-primary hover:text-white'
              }`}
            >
              Të gjitha
            </button>
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => setActiveCategory(category)}
                className={`px-5 py-2 rounded-lg border font-medium transition-colors ${
                  activeCategory === category
                    ? 'bg-primary text-white border-primary'
                    : 'border-primary text-primary hover:bg-primary hover:text-white'
              }`}
              >
                {category}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground">Duke ngarkuar videot...</p>
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-16 bg-card rounded-2xl">
            <VideoIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Nuk ka video për momentin</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleVideos.map((video, index) => {
              const youtubeId = video.youtube_url ? getYouTubeId(video.youtube_url) : null;
              const thumbnailUrl = youtubeId
                ? `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`
                : 'https://images.unsplash.com/photo-1771536145073-b79d8d6ab218?crop=entropy&cs=srgb&fm=jpg&q=85';

              return (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: index * 0.04 }}
                  className="bg-card rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition-shadow"
                >
                  <div
                    className={`relative h-48 bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center group ${
                      youtubeId ? 'cursor-pointer' : ''
                    }`}
                    style={{
                      backgroundImage: `url(${thumbnailUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                    onClick={() => youtubeId && setPlayingVideo(video)}
                  >
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors" />
                    {youtubeId && (
                      <div className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <PlayCircle className="w-3 h-3" />
                        YouTube
                      </div>
                    )}
                    <PlayCircle className="w-16 h-16 text-white relative z-10 group-hover:scale-110 transition-transform" />
                  </div>

                  <div className="p-4">
                    <h2 className="font-heading font-semibold text-lg line-clamp-2 mb-3">
                      {video.title}
                    </h2>
                    <div className="flex items-center justify-between">
                      <span className="text-sm px-3 py-1 bg-primary/10 text-primary rounded-full">
                        {getCategoryLabel(video.category)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(video.upload_date).toLocaleDateString('sq-AL')}
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {playingVideo && getYouTubeId(playingVideo.youtube_url) && (
          <div
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setPlayingVideo(null)}
          >
            <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setPlayingVideo(null)}
                className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
              >
                <X className="w-8 h-8" />
              </button>
              <div className="aspect-video bg-black rounded-xl overflow-hidden">
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeId(playingVideo.youtube_url)}?autoplay=1`}
                  title={playingVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
              <h2 className="text-white text-xl font-semibold mt-4">{playingVideo.title}</h2>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default VideosPage;
